import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessagesSquare, Send, X } from "lucide-react";
import { API } from "../constants";
import { useLang } from "../i18n";

export default function ChatWidget() {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (e) => {
    e?.preventDefault();
    const txt = text.trim();
    if (!txt || streaming) return;
    const history = [...messages, { role: "user", text: txt }];
    setMessages([...history, { role: "assistant", text: "" }]);
    setText("");
    setStreaming(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: history.map(({ role, text: t2 }) => ({ role, text: t2 })), lang }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let reply = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        reply += dec.decode(value, { stream: true });
        setMessages((m) => [...m.slice(0, -1), { role: "assistant", text: reply }]);
      }
      if (!reply) throw new Error("empty");
    } catch {
      setMessages((m) => [...m.slice(0, -1), { role: "assistant", text: t("chat.err") }]);
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      <button
        data-testid="chat-open"
        onClick={() => setOpen(!open)}
        aria-label={t("chat.title")}
        className="fixed bottom-6 end-6 z-[80] flex h-14 w-14 items-center justify-center rounded-full bg-gold text-obsidian shadow-[0_10px_40px_rgba(212,175,55,0.35)] transition-transform duration-300 hover:scale-110"
      >
        {open ? <X size={22} /> : <MessagesSquare size={22} />}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 end-6 z-[80] flex h-[480px] w-[360px] max-w-[calc(100vw-3rem)] flex-col border border-gold/25 bg-cellar/95 backdrop-blur-xl"
          >
            <div className="border-b border-gold/20 px-5 py-4">
              <p className="font-display text-xs tracking-[0.3em] text-gold">{t("chat.title")}</p>
            </div>
            <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
              <p data-testid="chat-welcome" className="max-w-[85%] border border-gold/20 bg-obsidian/60 px-4 py-3 text-sm leading-relaxed text-parchment/85">
                {t("chat.welcome")}
              </p>
              {messages.map((m, i) => (
                <p
                  key={i}
                  data-testid={`chat-msg-${i}`}
                  className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed ${
                    m.role === "user" ? "ms-auto bg-gold/15 text-parchment" : "border border-gold/20 bg-obsidian/60 text-parchment/85"
                  }`}
                >
                  {m.text}
                </p>
              ))}
            </div>
            <form onSubmit={send} className="flex items-center gap-2 border-t border-gold/20 px-3 py-3">
              <input
                data-testid="chat-input"
                className="io-input !py-2 text-sm"
                placeholder={t("chat.placeholder")}
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
              <button
                type="submit"
                data-testid="chat-send"
                disabled={streaming || !text.trim()}
                aria-label={t("chat.send")}
                className="flex h-10 w-10 shrink-0 items-center justify-center bg-gold text-obsidian transition-colors hover:bg-parchment disabled:opacity-40"
              >
                <Send size={16} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
